import { ASSIGNED_DIFFICULTIES } from '@all-for-one/shared-types'

/**
 * Parses an uploaded class list into students.
 *
 * This exists as its own module because the previous version lived inside the
 * upload overlay, could only be exercised by driving the UI, and was silently
 * corrupting live records. Everything here is pure: rows in, result out, no
 * React and no file handling, so it can be tested directly.
 *
 * WHAT WENT WRONG BEFORE — three faults that compounded, all of them the same
 * mistake in different clothes: an assumption about the file that was never
 * checked, and never surfaced when it was wrong.
 *
 *   1. The first three rows were discarded unconditionally, on the assumption
 *      every file carries the template's instruction block. A file without one
 *      lost three real students and said nothing.
 *   2. A row counted as headers if ANY ONE expected column name appeared in it.
 *      Names that did not match resolved to undefined and became empty strings,
 *      which is where 118 blank grades in production came from.
 *   3. With no header recognised, columns were read by POSITION. Nothing checked
 *      that a value suited its field, so a file with an email column one place
 *      earlier shifted every later field left by one — putting 133 student email
 *      addresses into `grade` and class section codes like "4A" into the field
 *      that decides a child's work level.
 *
 * The rule this module follows instead: when the file cannot be understood with
 * confidence, REFUSE IT AND SAY WHY. A teacher who is told "your Grade column
 * looks like it holds email addresses" can fix it in a minute. Silence cost us
 * 133 corrupted records and up to twelve children missing from rosters.
 */

/**
 * Splits one CSV line, respecting quotes.
 *
 * The upload path previously used `line.split(',')`, which breaks on any quoted
 * field containing a comma — `"Smith, Jr."` becomes two fields and every later
 * column shifts left by one. That is the same silent-shift fault as the
 * positional mapping above, arriving by a different route, so it is fixed here
 * rather than left as the one remaining way to corrupt a row unnoticed.
 * Spreadsheet uploads were never affected; the .xlsx reader handles quoting.
 */
export function splitCsvLine(line: string): string[] {
    const fields: string[] = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') { current += '"'; i++ }   // "" is a literal quote
                else inQuotes = false
            } else current += ch
        } else if (ch === '"') {
            inQuotes = true
        } else if (ch === ',') {
            fields.push(current.trim()); current = ''
        } else current += ch
    }
    fields.push(current.trim())
    return fields
}

/** A student as the create-class form expects them. */
export interface ParsedStudent {
    schoolStudentId: string
    firstName: string
    lastName: string
    grade: string
    difficulty: string
}

export interface ParseResult {
    /** False means do not use `students` — show `errors` to the teacher instead. */
    ok: boolean
    students: ParsedStudent[]
    /** Blocking problems, written to be read by a teacher, not a developer. */
    errors: string[]
    /** Worth telling them, but not a reason to reject the file. */
    warnings: string[]
}

/**
 * The only difficulty values the rest of the app understands.
 *
 * Note 'Not assigned' — lower-case 'a'. That is what the shared Difficulty type
 * permits and what difficultyToCssClass has an entry for. The old importer
 * defaulted to 'Not Assigned' with a capital A, which is not a valid Difficulty;
 * 31 production records carry it and have no matching CSS class. Strict type
 * checking is switched off in this package, so nothing ever objected.
 */
export const NOT_ASSIGNED = 'Not assigned'
const VALID_DIFFICULTIES: string[] = [...Object.values(ASSIGNED_DIFFICULTIES), NOT_ASSIGNED]

/**
 * Column names we recognise, including the ones a school's own export is likely
 * to use. Being generous here means fewer files rejected for cosmetic reasons —
 * but a column we cannot identify is still refused rather than guessed at.
 */
const COLUMN_ALIASES: Record<keyof ParsedStudent, string[]> = {
    schoolStudentId: ['schoolstudentid', 'studentid', 'studentnumber', 'schoolid', 'id'],
    lastName: ['lastname', 'last', 'surname', 'familyname'],
    firstName: ['firstname', 'first', 'givenname', 'forename'],
    grade: ['grade', 'gradelevel', 'year', 'yeargroup'],
    // 'learninglevel' is what OUR OWN downloadable template calls this column.
    // Missing it meant every student from the official template imported as
    // 'Not assigned' — the aliases were written from what the code called the
    // field rather than from what the file teachers actually download says.
    difficulty: ['difficulty', 'learninglevel', 'level', 'readinglevel', 'tier'],
}

const REQUIRED_COLUMNS: (keyof ParsedStudent)[] = ['firstName', 'lastName']

const normalise = (v: unknown) => String(v ?? '').trim()
const normaliseHeader = (v: unknown) => normalise(v).toLowerCase().replace(/[\s_-]+/g, '')

const looksLikeEmail = (v: string) => /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(v)
/** K and T cover kindergarten and transitional, both present in live data. */
const looksLikeGrade = (v: string) => /^(K|T|PK|\d{1,2})$/i.test(v)

const isBlankRow = (row: unknown[]) =>
    !Array.isArray(row) || row.every(cell => normalise(cell) === '')

/**
 * True for a row of guidance text rather than a student.
 *
 * Our own template puts per-column help text on the row DIRECTLY BELOW the
 * headers — "This is required", "Grade should be a number unless it is
 * Kindergarten…" — and tells the teacher to start typing on the row after it.
 * Without this, that row imports as a child named "This is required".
 *
 * Detected by shape, not by position, so it also catches a teacher who keeps
 * the guidance and starts their data lower down: real names are short and are
 * not sentences.
 */
const looksLikeGuidance = (row: unknown[]) => {
    const cells = row.map(normalise).filter(c => c !== '')
    if (cells.length === 0) return false
    return cells.some(c => c.length > 60 || /\.\s/.test(c) || /^\*{2,}/.test(c))
}

/**
 * Finds the header row rather than assuming a fixed number of instruction rows.
 *
 * Requires at least two recognisable columns before treating a row as headers.
 * One is not enough: a single match is what let a data row masquerade as a
 * header before, and a genuine header row essentially always has several.
 */
const findHeaderRow = (rows: unknown[][]): { index: number; map: Partial<Record<keyof ParsedStudent, number>> } | null => {
    const searchLimit = Math.min(rows.length, 15)

    for (let i = 0; i < searchLimit; i++) {
        if (isBlankRow(rows[i])) continue
        const cells = rows[i].map(normaliseHeader)
        const map: Partial<Record<keyof ParsedStudent, number>> = {}

        for (const field of Object.keys(COLUMN_ALIASES) as (keyof ParsedStudent)[]) {
            const idx = cells.findIndex(c => c !== '' && COLUMN_ALIASES[field].includes(c))
            if (idx >= 0) map[field] = idx
        }

        if (Object.keys(map).length >= 2) return { index: i, map }
    }
    return null
}

export function parseClassList(rows: unknown[][]): ParseResult {
    const errors: string[] = []
    const warnings: string[] = []

    const nonEmpty = (rows || []).filter(r => !isBlankRow(r))
    if (nonEmpty.length === 0) {
        return { ok: false, students: [], errors: ['This file appears to be empty.'], warnings }
    }

    const header = findHeaderRow(nonEmpty)

    // No recognisable header means we would be guessing at column order, which
    // is exactly what corrupted the live records. Refuse instead of guessing.
    if (!header) {
        return {
            ok: false,
            students: [],
            errors: [
                'We could not find a header row in this file, so we cannot tell which column is which.',
                'Please add a first row naming the columns — for example: schoolStudentId, lastName, firstName, grade, difficulty.',
                'Downloading the class list template and pasting your students into it is the quickest way.',
            ],
            warnings,
        }
    }

    const missingRequired = REQUIRED_COLUMNS.filter(f => header.map[f] === undefined)
    if (missingRequired.length > 0) {
        const readable: Record<string, string> = { firstName: 'First Name', lastName: 'Last Name' }
        return {
            ok: false,
            students: [],
            errors: [
                `We found a header row, but not a column for ${missingRequired.map(f => readable[f] || f).join(' or ')}.`,
                'Every student needs a first and last name. Please check the column headings and upload again.',
            ],
            warnings,
        }
    }

    if (header.map.difficulty === undefined) {
        warnings.push(`No level column was found, so every student has been set to "${NOT_ASSIGNED}". You can change levels afterwards.`)
    }
    if (header.map.grade === undefined) {
        warnings.push('No grade column was found, so grades have been left blank.')
    }

    const dataRows = nonEmpty.slice(header.index + 1).filter(r => !looksLikeGuidance(r))
    if (dataRows.length === 0) {
        return { ok: false, students: [], errors: ['We found column headings but no students underneath them.'], warnings }
    }

    const cell = (row: unknown[], field: keyof ParsedStudent) => {
        const idx = header.map[field]
        return idx === undefined ? '' : normalise(row[idx])
    }

    const students: ParsedStudent[] = []
    const badGrades: number[] = []
    const badLevels: string[] = []
    const emailsInWrongField: string[] = []
    let skippedBlankName = 0

    dataRows.forEach((row, n) => {
        const rowNumber = n + 1
        const firstName = cell(row, 'firstName')
        const lastName = cell(row, 'lastName')
        const grade = cell(row, 'grade')
        const rawDifficulty = cell(row, 'difficulty')
        const schoolStudentId = cell(row, 'schoolStudentId')

        // An email where a name, grade or level belongs is the signature of a
        // shifted file. This is the specific fault that reached production.
        for (const [label, value] of [['Grade', grade], ['Level', rawDifficulty], ['First Name', firstName], ['Last Name', lastName]] as const) {
            if (value && looksLikeEmail(value) && !emailsInWrongField.includes(label)) {
                emailsInWrongField.push(label)
            }
        }

        if (!firstName && !lastName) {
            skippedBlankName++
            return
        }

        if (grade && !looksLikeGrade(grade) && !looksLikeEmail(grade)) badGrades.push(rowNumber)

        let difficulty = rawDifficulty
        if (difficulty) {
            const matched = VALID_DIFFICULTIES.find(v => v.toLowerCase() === difficulty.toLowerCase())
            if (matched) {
                difficulty = matched      // repair casing, e.g. "advanced" -> "Advanced"
            } else {
                if (!badLevels.includes(difficulty)) badLevels.push(difficulty)
            }
        } else {
            difficulty = NOT_ASSIGNED
        }

        students.push({ schoolStudentId, firstName, lastName, grade, difficulty })
    })

    // Emails in the wrong column mean the whole file is shifted. Nothing here is
    // trustworthy, so reject rather than import a subset.
    if (emailsInWrongField.length > 0) {
        errors.push(
            `The ${emailsInWrongField.join(' and ')} column${emailsInWrongField.length > 1 ? 's appear' : ' appears'} to contain email addresses.`,
            'This usually means the columns in your file are in a different order than the headings suggest.',
            'Nothing has been imported. Please check the file and upload it again.',
        )
        return { ok: false, students: [], errors, warnings }
    }

    if (badLevels.length > 0) {
        errors.push(
            `These do not look like levels: ${badLevels.slice(0, 5).map(v => `"${v}"`).join(', ')}${badLevels.length > 5 ? ` and ${badLevels.length - 5} more` : ''}.`,
            `A level must be one of: ${VALID_DIFFICULTIES.join(', ')}.`,
            'Nothing has been imported.',
        )
        return { ok: false, students: [], errors, warnings }
    }

    if (students.length === 0) {
        return { ok: false, students: [], errors: ['We could not read any students from this file.'], warnings }
    }

    if (badGrades.length > 0) {
        warnings.push(`The grade could not be understood for ${badGrades.length} student${badGrades.length > 1 ? 's' : ''}. Those grades have been kept as they are — please check them.`)
    }
    if (skippedBlankName > 0) {
        warnings.push(`${skippedBlankName} row${skippedBlankName > 1 ? 's were' : ' was'} skipped because ${skippedBlankName > 1 ? 'they had' : 'it had'} no name.`)
    }

    return { ok: true, students, errors, warnings }
}
